// ═══════════════════════════════════════════════════════════════════
// vaelModel3D.jsx — V'AEL, SCULPTED   (caP'n, 2026-09-16, session 3)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "is it possible to make V'ael in Terror 3D plaza look exactly
// like her picture?" → the one-model-file exception to the "no .glb"
// hold, approved for her alone: "just V'ael, let's see how well we can
// get her looking first."  Then: "no test switch, we will plug her in …
// add her in the 2D version with this new 3D model like we did the
// companions. This is the Unstable World so her actually being a
// dimension higher is canon anyways."
//
// WHAT IT IS. `public/models/vael.glb` — V'ael sculpted off her idle
// front sheet, the standing profile and the back sheet.  The builder is
// gamemaster/models/sculpt/builders/vael_blender/ (run inside Blender);
// the master glb is gamemaster/models/sculpt/glb/vael.glb and this copy
// is never hand-edited.  Metres, feet at y=0, facing +z, 12 named meshes
// on ONE 21-BONE SKELETON: root/hips/spine/chest/neck/head, both legs
// (thigh/shin/foot), both arms (upperarm/forearm/hand) and a 3-bone tail
// (since 2026-09-27 the full IE Humanoid Rig on top of those — see below).
// The walk and idle are driven here from ctl.speed — no clips in the file.
//
// TWO DOORS, ONE BODY
//   <VaelModel ctl h>      — inside an existing r3f scene (Plaza3D,
//                            the folk review room). Same {ctl, h}
//                            contract as the painted VaelBody.
//   <VaelPortrait3D ...>   — a body in a DOM box, for the 2D screens
//                            (Onboarding's stage), exactly the
//                            CompanionBody3D pattern: one transparent
//                            canvas of the box's size.
//
// ⭐ SHE ACTS (2026-09-24, the acting pass). A body mounted with `acting`
// takes MOODS for cutscenes — faces, finger poses, gestures, a sundae —
// all in vaelActing.js (moods) and vaelHands.js (finger shapes). The 2D
// door acts; the Plaza body does not, and looks exactly as it always has.
//   <VaelPortrait3D mood="flustered" talking shot="waist" />
// The file now carries 30 finger bones after the 21 body bones; her hand
// props live beside it in vael_props.glb, fetched only by an acting body.
//
// ⭐ THE HUMANOID RIG (2026-09-27, the rig-up session). vael.glb is now an IE
// HUMANOID RIG (IEH1, 116 joints: + spine2, clavicles, arm twists, toes, 4 more tail
// joints and the 12×4 SKIRT RING) and carries its own rig data (`ieh` extras). ieRig.js
// reads it: one begin() → apply() → end() per frame below. What that gives her:
//   · ctl.clip = MOVES.sit_chair (ieMoves.js) — sit, kneel, lie down, the worm, the
//     robot, the Lean Back, punches, a curtsy … on top of the walk and the moods;
//   · the skirt, tail (and, acting, her ram dreads) are PHYSICS chains: they follow
//     her legs, hang with gravity, trail when she runs, drape over a chair and pool
//     on the floor (ctl.colliders: the scene's seats and steps);
//   · THE WARDROBE: wearPart() puts a garment FILE on her by bone name — changing
//     clothes never needs a re-rig. The standard and the workflow:
//     gamemaster/models/sculpt/IE_HUMANOID_RIG.md. Old glbs get a rig that does nothing.
//
// ⭐ THE FALLBACK IS THE OLD BODY. Load failure (file missing, CDN down)
// or the first frame before the file arrives renders the painted
// VaelBody that shipped before this — nothing ever goes blank.
//
// LOOK. Toon shading (3-step ramp) + an inverted-hull ink outline, no
// post-processing and no new lights: the scene's own lights shade her.
// PERF LAW: nothing here sets React state from a frame.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { makeActor } from "./vaelActing";
import { makeRig, attachPart } from "./ieRig";
import { applyForm, formPart } from "./ieForm";

export const VAEL_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/vael.glb`;
// her hand props (the sundae, its spoon) — a separate file, fetched only by an acting body
export const VAEL_PROPS_URL = `${process.env.PUBLIC_URL || ""}/models/vael_props.glb`;
const MODEL_TOP = 1.76;              // crown of her head in the file (horns/bun rise above)
// THE INK HULL'S WIDTH, PER PART — mirrored in the builder's vael_look.py;
// if the two drift, every review render lies about what the game draws.
// The hull is the part pushed w along its normals and drawn back-faces-only, so
// on a THIN part a full-width hull comes out wider than the part itself. Her
// cord is a 4.4 mm tube and her flat collar a 5.2 mm shell; both used to get no
// line at all, which left the collar reading as a white blob against her skin
// (caP'n, 2026-09-22: "yes give them outlines looks too white"). They get a
// NARROWER line instead of none. Her stand collar is 4 mm but SOLIDIFIED and
// closed, so it takes the full width like everything else. 0 would mean no hull.
const INK_W = 0.0032;
const INK_BY_PART = [[/cord/, 0.0014], [/capelet/, 0.0024], [/vael_prop_(glass|cherry|spoon|bite)/, 0.0012], [/vael_prop_/, 0.0018]];
const inkWidth = (name) => (INK_BY_PART.find(([re]) => re.test(name)) || [null, INK_W])[1];
const SELF_LIGHT = 0.45;         // 0 = fully at the mercy of room light, 1 = unlit flat colour

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const INK = new Map();               // one material per width (and per stepping/plain)
// THE INK STEPS BACK IN A CREASE (body pass 2, 2026-09-30). Where a raised arm presses the sleeve's
// underside into her side, the two walls of that crease meet and one wall's outline came out through
// the other: a black jagged "pit blip". A mesh may carry a per-vertex `_inkb` (0–1, written by the
// rig-up's tools/rigup/ink_ease.py from the raised-arm poses of the battery: only where two walls of
// that mesh really press together). There the hull is also pushed `_inkb × INK_BACK` AWAY from the
// camera along the view ray: same place on screen, but it loses the depth test to the wall in front.
// Those spots sit hidden under the arm at rest, so resting looks are untouched; panel edges, fingers,
// the collar and the bust carry 0. A mesh without `_inkb` draws the plain hull, exactly as before.
// (Tried and dropped: thinning the hull — the bust lost its line; pushing EVERY hull back — panel
// edges, finger and arm-underside lines faded. See IE_HUMANOID_RIG.md §10d.)
const INK_BACK = 0.005;
const inkMat = (w, step = false) => {
  const key = w + (step ? "|b" : "");
  let m = INK.get(key);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace(
      "#include <begin_vertex>",
      "#include <begin_vertex>\n  transformed += normalize( normal ) * " + w.toFixed(5) + ";"
    );
    if (step) sh.vertexShader = "attribute float _inkb;\n" + sh.vertexShader.replace(
      "#include <project_vertex>",
      // (away from the camera: along the view ray for a perspective camera, straight back for an orthographic one)
      "#include <project_vertex>\n  mvPosition.xyz += ( projectionMatrix[ 2 ][ 3 ] != 0.0 ? normalize( mvPosition.xyz ) : vec3( 0.0, 0.0, -1.0 ) ) * ( _inkb * " + INK_BACK.toFixed(5) + " ) * length( modelViewMatrix[ 0 ].xyz );\n  gl_Position = projectionMatrix * mvPosition;"
    );
  };
  // ⚠ three caches compiled programs by material signature, and two widths are
  // the same signature — without this key the second one silently reuses the
  // first one's shader and every part gets whichever width compiled first.
  m.customProgramCacheKey = () => "vaelInk" + key;
  INK.set(key, m);
  return m;
};

// one prepared copy per mounted body (geometry + textures stay shared)
// acting = this body performs (cutscenes): its face layers, fingers, props and
// pop-ups come alive (vaelActing.js). A body that doesn't act is exactly the
// Plaza V'ael she has always been.
// form = THE FORM (ieForm.js): her proportions, e.g. { armR: { scale: 1.8 }, forearmL: { girth: [1, 1.4] } } —
// applied to this body's own copy of the geometry BEFORE the ink and the rig, so both see the new shape
function prepare(scene, acting, form) {
  const root = cloneSkinned(scene);              // a plain clone would share one skeleton between bodies
  // collect first: adding the ink children DURING traverse walks them too, forever
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  // ONE SKELETON for all her meshes (the loader gives each mesh its own copy of the same
  // bones: 12x the bone-matrix work and bone-texture uploads per frame, for nothing)
  const shared = (meshes.find((o) => o.isSkinnedMesh) || {}).skeleton;
  if (shared) meshes.forEach((o) => { if (o.isSkinnedMesh && o.skeleton !== shared) o.bind(shared, o.bindMatrix); });
  if (form) { try { applyForm(root, form); } catch (e) { console.warn("[vael] the form failed — she keeps her own shape", e); } }
  meshes.forEach((o) => dressMesh(o, acting));
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  const actor = acting ? makeActor(root, B, loadProps) : null;
  // THE HUMANOID RIG (ieRig.js): clips, IK, floor contact, clavicles/twists and the skirt/tail
  // chains — all read from the glb's own `ieh` extras; an older glb gets a rig that does nothing
  // hair: true on EVERY body — her ram dreads bounce in the Plaza walk too (caP'n's V'AEL ROADMAP
  // item 3, approved 2026-09-24: "let the dreads bounce in the Plaza too, when she walks")
  const rig = makeRig(root, B, { hair: true });
  // a cutscene (or the rig lab) reaches it through the model — NOT enumerable: Object3D.clone()
  // and toJSON() copy userData through JSON, and the rig holds bones (a cycle)
  Object.defineProperty(root.userData, "rig", { value: rig, enumerable: false, writable: true });
  root.userData.acting = !!acting;
  return { root, B, actor, rig };
}
// THE WARDROBE: put a garment file on a mounted V'ael — any glb skinned to her joint NAMES
// (the rig-up's bind_garment + extract_parts writes them). It is dressed exactly like her own
// clothes (toon + ink) and the rig's cloth/hair chains follow what she wears:
//   const worn = wearPart(root, gltf.scene, { replace: ["vael_skirt"] });   …   worn.off();
// root = the model's root (the object whose userData.rig is set: prepare's `root`)
export function wearPart(root, partScene, o = {}) {
  const worn = attachPart(root, cloneSkinned(partScene), { ...o, dress: (m) => dressMesh(m, !!root.userData.acting), fit: formPart });
  // a garment FITTED to a formed body owns its geometry: freed when it comes off (not only when she goes)
  const fm = root.userData.form;
  if (worn && fm && fm.geos) {
    const off = worn.off;
    worn.off = () => {
      off();
      for (const m of worn.meshes) { const i = fm.geos.indexOf(m.geometry); if (i >= 0) { fm.geos.splice(i, 1); m.geometry.dispose(); } }
    };
  }
  return worn;
}
// the props file, loaded once and dressed like her body; each actor gets its own copy
let PROPS = null;
function loadProps(cb) {
  if (!PROPS) {
    PROPS = new Promise((res, rej) => new GLTFLoader().load(VAEL_PROPS_URL, (g) => res(g.scene), undefined, rej));
    PROPS.catch((e) => console.warn("[vael] vael_props.glb did not load — she acts without her sundae", e));
  }
  PROPS.then((scene) => {
    const copy = scene.clone(true);
    const meshes = [];
    copy.traverse((o) => { if (o.isMesh) meshes.push(o); });
    meshes.forEach((o) => { o.material = o.material.clone(); dressMesh(o, true); });
    cb(copy);
  }, () => {});
}
// toon material + ink hull for one mesh (body parts and props alike)
function dressMesh(o, acting) {
  const m = o.material;
  if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
  // her own colours glow a little from within, so a violet or ember room
  // tints her instead of repainting her (the painted body's same trick)
  o.material = new THREE.MeshToonMaterial({
    color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side,
    emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
  });
  o.frustumCulled = false;                     // skinned bounds don't follow the pose
  // an ACTING body bends her elbows hard (a spoon to her mouth), and the sleeve
  // folds over itself at the inner elbow; drawn one-sided, the fold's back faces
  // vanish and her skin shows through as a white slash. Both sides for her dress.
  if (acting && o.name.startsWith("vael_dress")) o.material.side = THREE.DoubleSide;
  if (/^vael_prop_glass/.test(o.name)) {       // the sundae glass is see-through
    o.material.transparent = true; o.material.opacity = 0.5; o.material.depthWrite = false;
  }
  const inkW = inkWidth(o.name);
  if (inkW > 0) {
    const ink = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(inkW, !!o.geometry.attributes._inkb)) : new THREE.Mesh(o.geometry, inkMat(inkW, !!o.geometry.attributes._inkb));
    if (o.isSkinnedMesh) ink.bind(o.skeleton, o.bindMatrix);
    ink.name = o.name + "_ink";
    ink.frustumCulled = false;
    o.parent.add(ink);
  }
}
// the bones a pose may leave untouched: zeroed each frame so an actor's
// "on top of the idle" never builds on its own last frame
const RESET = ["neck", "handL", "handR"];

// THE WALK — one stride per phase turn; amt 0 = standing, 1 = full walk.
// Short steps on purpose: a matriarch in a floor-length dress.
function vaelPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(ph);
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off;
    const fwd = Math.sin(lp);
    const swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, -fwd * 0.3 * amt - swing * 0.12 * amt, 0, 0);
    set("shin" + k, swing * 0.55 * amt, 0, 0);
    set("foot" + k, -swing * 0.3 * amt + Math.max(0, -fwd) * 0.15 * amt, 0, 0);
    set("upperarm" + k, fwd * 0.24 * amt, 0, s * 0.04);
    set("forearm" + k, -0.12 - Math.max(0, -fwd) * 0.25 * amt, 0, 0);
  }
  set("hips", 0, sw * 0.07 * amt, Math.sin(t * 0.5) * 0.02 * (1 - amt));
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.022 * amt;
  set("spine", 0.02 * amt, -sw * 0.05 * amt, 0);
  set("chest", Math.sin(t * 1.5) * 0.015, -sw * 0.05 * amt, 0);
  set("head", -0.03 + Math.sin(t * 0.8) * 0.02, Math.sin(t * 0.42) * 0.28 * (1 - 0.6 * amt), Math.sin(t * 0.27) * 0.04);
  set("tail1", Math.sin(t * 0.9 + 1) * 0.05, 0, Math.sin(t * 1.3) * 0.1);
  set("tail2", 0, 0, Math.sin(t * 1.3 - 0.7) * 0.12);
  set("tail3", 0, 0, Math.sin(t * 1.3 - 1.4) * 0.15);
}

export function VaelModel({ ctl, h = 1.5, acting = false, form = null }) {
  const gltf = useLoader(GLTFLoader, VAEL_GLB_URL);
  const formKey = form ? JSON.stringify(form) : "";          // a new form = a freshly prepared body (the rig re-measures it)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { root, B, actor, rig } = useMemo(() => prepare(gltf.scene, acting, form), [gltf, acting, formKey]);
  // a formed body owns its geometry (ieForm.js cloned it): free it when this body goes (or its form changes)
  useEffect(() => () => { const fm = root.userData.form; if (fm && fm.geos) for (const g of fm.geos) g.dispose(); }, [root]);
  const clipOut = useRef(null);
  const rigDead = useRef(false);                 // a rig error turns the rig off for this body — never the canvas
  const safe = (f) => {
    if (rigDead.current) return null;
    try { return f(); } catch (e) {
      rigDead.current = true;
      console.warn("[vael] the rig failed — she carries on without it", e);
      try { rig.begin(); } catch (e2) { /* nothing more to undo */ }
      return null;
    }
  };
  const amt = useRef(0);
  const g = useRef();
  const yaw = useRef(0);
  const ph = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 1.3;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    const speed = (ctl && ctl.speed) || 0; ph.current += (speed ? 1 : amt.current) * dt * 7;
    o.rotation.y = yaw.current;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);   // ease into and out of the walk
    safe(() => rig.begin());                     // first: undo last frame's post layers (THE PARITY LAW)
    const co = clipOut.current;                  // last frame's clip hands/face ride the mood layer (acting bodies)
    // (hands at w > 0.25: a punch combo started cold threw its first jab open-handed)
    if (actor && ctl) { ctl.clipHands = co && co.w > 0.25 ? co.hands : null; ctl.clipFace = co && co.w > 0.3 ? co.face : null; ctl.clipFingers = co && co.w > 0.5 ? co.fingers : null; }
    if (actor) {
      for (const n of RESET) if (B[n]) B[n].rotation.set(0, 0, 0);
      const w = actor.walk;                      // a mood may stroll in place (bliss)
      const useW = !speed && w.amt > amt.current;
      vaelPose(B, t, useW ? w.ph : ph.current, useW ? w.amt : amt.current);
      actor.update(t, dt, ctl);
    } else {
      vaelPose(B, t, ph.current, amt.current);
    }
    clipOut.current = safe(() => rig.apply(t, dt, ctl));   // ctl.clip: a move from ieMoves.js, over everything
    safe(() => rig.end(dt));                     // clavicles, twists, then the skirt and tail chains
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / MODEL_TOP} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[vael] vael.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// the Plaza door: sculpted body, painted body as the safety net
export function VaelSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <VaelModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

// the 2D door: V'ael in a DOM box (Onboarding's stage, cutscenes). One canvas.
// mood / talking / eyes / mouth / fx / pops: see vaelActing.js — e.g.
//   <VaelPortrait3D mood="flustered" talking />
// shot: "full" (default, the Onboarding framing) | "waist" | "face" — or pass
// camera={{ position, look, fov }} for anything else
const SHOTS = {
  full: { position: [0, 0.95, 5.4], look: [0, 0.9, 0], fov: 22 },
  waist: { position: [0, 1.28, 2.9], look: [0, 1.22, 0], fov: 22 },
  face: { position: [0, 1.52, 1.35], look: [0, 1.52, 0], fov: 22 },
};
export function VaelPortrait3D({ facing = { x: -0.35, y: 1 }, style, onFail, mood, talking, eyes, mouth, fx, pops, blink, shot = "full", camera }) {
  const cam = camera || SHOTS[shot] || SHOTS.full;
  const ctl = useRef({ facing, speed: 0 }).current;
  ctl.facing = facing;
  ctl.mood = mood; ctl.talking = !!talking; ctl.eyes = eyes; ctl.mouth = mouth; ctl.fx = fx; ctl.pops = pops; ctl.blink = blink;
  return (
    <Canvas
      dpr={[1, 1.5]}
      gl={{ alpha: true, antialias: true, powerPreference: "low-power" }}
      camera={{ position: cam.position, fov: cam.fov || 22, near: 0.05, far: 30 }}
      onCreated={(s) => { s.gl.setClearColor(0x000000, 0); s.camera.lookAt(...(cam.look || [0, 0.9, 0])); }}
      style={{ width: "100%", height: "100%", background: "transparent", pointerEvents: "none", ...style }}
    >
      <hemisphereLight args={["#fff4ee", "#6a4a48", 1.2]} />
      <directionalLight position={[1.5, 3, 4]} intensity={1.6} color="#fff0e0" />
      <directionalLight position={[-3, 2, -2]} intensity={0.6} color="#ff8a6a" />
      <GlbBoundary fallback={<FailSignal onFail={onFail} />}>
        <Suspense fallback={null}>
          <VaelModel ctl={ctl} h={1.76} acting />
        </Suspense>
      </GlbBoundary>
    </Canvas>
  );
}
function FailSignal({ onFail }) {
  const done = useRef(false);
  useFrame(() => { if (!done.current) { done.current = true; onFail && onFail(); } });
  return null;
}

export default VaelModel;
